import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SaveIcon, UserIcon, PhoneIcon, HomeIcon, DollarSignIcon, CalendarIcon, FileTextIcon, HashIcon, BuildingIcon, UploadIcon, CheckCircleIcon, CheckIcon, AlertCircleIcon } from 'lucide-react';
interface AddViewingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (viewingData: any) => void;
  viewingType?: 'inchiriere' | 'vanzare';
  hasCompletedRentalTransaction?: boolean;
  editMode?: boolean;
  existingViewing?: any;
}
interface ViewingData {
  viewingId: string;
  dateTime: string;
  // Date Client
  leadId: string;
  clientName: string;
  clientPhone: string;
  clientIsExternal: boolean;
  clientAgencyName?: string;
  clientAgentName?: string;
  clientAgentPhone?: string;
  clientContractOption?: 'signed' | 'physical_signing';
  clientContractFile?: File | null;
  // Comisionare
  commissionPaidBy: 'owner' | 'client' | 'both';
  ownerCommission?: number;
  ownerVatOption?: 'included' | 'additional';
  ownerDiscountRequested?: boolean;
  clientCommission?: number;
  clientVatOption?: 'included' | 'additional';
  clientDiscountRequested?: boolean;
  // Date Proprietar
  ownerId: string;
  ownerName: string;
  ownerPhone: string;
  ownerIsExternal: boolean;
  ownerAgencyName?: string;
  ownerAgentName?: string;
  ownerAgentPhone?: string;
  ownerContractOption?: 'signed' | 'physical_signing';
  ownerContractFile?: File | null;
  // Date Proprietate
  propertyId: string;
  propertyAddress: string;
  propertyPrice: number;
  // Data și Ora Vizionare
  viewingDate: string;
  viewingTime: string;
  notes: string;
}
export function AddViewingDrawer({
  isOpen,
  onClose,
  onSubmit,
  viewingType: initialViewingType,
  hasCompletedRentalTransaction = false,
  editMode = false,
  existingViewing = null
}: AddViewingDrawerProps) {
  const [step, setStep] = useState<'select_type' | 'form'>('select_type');
  const [selectedType, setSelectedType] = useState<'inchiriere' | 'vanzare' | null>(initialViewingType || null);
  const [viewingData, setViewingData] = useState<ViewingData>({
    viewingId: '',
    dateTime: '',
    leadId: '',
    clientName: '',
    clientPhone: '',
    clientIsExternal: false,
    clientContractOption: undefined,
    clientContractFile: null,
    commissionPaidBy: 'owner',
    ownerVatOption: 'included',
    ownerDiscountRequested: false,
    clientVatOption: 'included',
    clientDiscountRequested: false,
    ownerId: '',
    ownerName: '',
    ownerPhone: '',
    ownerIsExternal: false,
    ownerContractOption: undefined,
    ownerContractFile: null,
    propertyId: '',
    propertyAddress: '',
    propertyPrice: 0,
    viewingDate: '',
    viewingTime: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Initialize ID and Date on open
  useEffect(() => {
    if (isOpen) {
      // Edit mode: skip type selection and populate with existing data
      if (editMode && existingViewing) {
        setStep('form');
        setSelectedType(existingViewing.viewingType || 'inchiriere');
        setViewingData({
          viewingId: existingViewing.id,
          dateTime: existingViewing.createdAt ? new Date(existingViewing.createdAt).toLocaleString('ro-RO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }) : '',
          leadId: existingViewing.leadId || '',
          clientName: existingViewing.clientName || '',
          clientPhone: existingViewing.clientPhone || '',
          clientIsExternal: existingViewing.clientIsExternal || false,
          clientAgencyName: existingViewing.clientAgencyName || '',
          clientAgentName: existingViewing.clientAgentName || '',
          clientAgentPhone: existingViewing.clientAgentPhone || '',
          clientContractOption: existingViewing.clientContractSigned ? 'signed' : existingViewing.clientSignPhysically ? 'physical_signing' : undefined,
          clientContractFile: existingViewing.clientContractFile || null,
          commissionPaidBy: existingViewing.commissionPaidBy || 'owner',
          ownerCommission: existingViewing.ownerCommission || undefined,
          ownerVatOption: existingViewing.ownerVatOption || 'included',
          ownerDiscountRequested: existingViewing.ownerDiscountRequested || false,
          clientCommission: existingViewing.clientCommission || undefined,
          clientVatOption: existingViewing.clientVatOption || 'included',
          clientDiscountRequested: existingViewing.clientDiscountRequested || false,
          ownerId: existingViewing.ownerId || '',
          ownerName: existingViewing.ownerName || '',
          ownerPhone: existingViewing.ownerPhone || '',
          ownerIsExternal: existingViewing.ownerIsExternal || false,
          ownerAgencyName: existingViewing.ownerAgencyName || '',
          ownerAgentName: existingViewing.ownerAgentName || '',
          ownerAgentPhone: existingViewing.ownerAgentPhone || '',
          ownerContractOption: existingViewing.ownerContractSigned ? 'signed' : existingViewing.ownerSignPhysically ? 'physical_signing' : undefined,
          ownerContractFile: existingViewing.ownerContractFile || null,
          propertyId: existingViewing.propertyId || '',
          propertyAddress: existingViewing.propertyAddress || '',
          propertyPrice: existingViewing.propertyPrice || 0,
          viewingDate: existingViewing.date || '',
          viewingTime: existingViewing.time || '',
          notes: existingViewing.agentNotes || ''
        });
      } else {
        // Create mode
        // Reset to type selection if no initial type provided
        if (!initialViewingType) {
          setStep('select_type');
          setSelectedType(null);
        } else {
          setStep('form');
          setSelectedType(initialViewingType);
        }
        const randomId = Math.floor(Math.random() * 90000) + 10000;
        setViewingData({
          viewingId: `VZ${randomId}`,
          dateTime: new Date().toLocaleString('ro-RO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          leadId: '',
          clientName: '',
          clientPhone: '',
          clientIsExternal: false,
          clientContractOption: undefined,
          clientContractFile: null,
          commissionPaidBy: 'owner',
          ownerVatOption: 'included',
          ownerDiscountRequested: false,
          clientVatOption: 'included',
          clientDiscountRequested: false,
          ownerId: '',
          ownerName: '',
          ownerPhone: '',
          ownerIsExternal: false,
          ownerContractOption: undefined,
          ownerContractFile: null,
          propertyId: '',
          propertyAddress: '',
          propertyPrice: 0,
          viewingDate: '',
          viewingTime: '',
          notes: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, initialViewingType, editMode, existingViewing]);
  const needsClientContract = viewingData.commissionPaidBy === 'client' || viewingData.commissionPaidBy === 'both';
  const needsOwnerContract = viewingData.commissionPaidBy === 'owner' || viewingData.commissionPaidBy === 'both';
  const updateData = (field: keyof ViewingData, value: any) => {
    setViewingData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Check if commission needs discount approval
  const needsOwnerDiscount = () => {
    if (!viewingData.ownerCommission) return false;
    const minRequired = viewingData.ownerVatOption === 'included' ? 50 : 40;
    return viewingData.ownerCommission < minRequired;
  };
  const needsClientDiscount = () => {
    if (!viewingData.clientCommission) return false;
    const minRequired = viewingData.clientVatOption === 'included' ? 50 : 40;
    return viewingData.clientCommission < minRequired;
  };
  const handleRequestOwnerDiscount = () => {
    updateData('ownerDiscountRequested', true);
    // Here you would typically send a request to the agency
    // For now, we just mark it as requested
  };
  const handleRequestClientDiscount = () => {
    updateData('clientDiscountRequested', true);
    // Here you would typically send a request to the agency
    // For now, we just mark it as requested
  };
  const handleFileUpload = (field: 'clientContractFile' | 'ownerContractFile', file: File) => {
    updateData(field, file);
  };
  const toggleClientExternal = () => {
    setViewingData(prev => {
      const newValue = !prev.clientIsExternal;
      return {
        ...prev,
        clientIsExternal: newValue,
        // Clear opposite fields
        ...(newValue ? {
          leadId: '',
          clientName: '',
          clientPhone: ''
        } : {
          clientAgencyName: '',
          clientAgentName: '',
          clientAgentPhone: ''
        })
      };
    });
  };
  const toggleOwnerExternal = () => {
    setViewingData(prev => {
      const newValue = !prev.ownerIsExternal;
      return {
        ...prev,
        ownerIsExternal: newValue,
        // Clear opposite fields
        ...(newValue ? {
          ownerId: '',
          ownerName: '',
          ownerPhone: ''
        } : {
          ownerAgencyName: '',
          ownerAgentName: '',
          ownerAgentPhone: ''
        })
      };
    });
  };
  const handleCommissionChange = (newValue: 'owner' | 'client' | 'both') => {
    setViewingData(prev => {
      const updates: Partial<ViewingData> = {
        commissionPaidBy: newValue
      };
      // Clear client contract if client is not paying
      if (newValue === 'owner') {
        updates.clientContractOption = undefined;
        updates.clientContractFile = null;
      }
      // Clear owner contract if owner is not paying
      if (newValue === 'client') {
        updates.ownerContractOption = undefined;
        updates.ownerContractFile = null;
      }
      return {
        ...prev,
        ...updates
      };
    });
  };
  const handleSelectType = (type: 'inchiriere' | 'vanzare') => {
    setSelectedType(type);
    setStep('form');
  };
  const handleBack = () => {
    if (step === 'form' && !initialViewingType) {
      setStep('select_type');
      setSelectedType(null);
    } else {
      onClose();
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Validate Client Data
    if (viewingData.clientIsExternal) {
      if (!viewingData.clientAgencyName?.trim()) newErrors.clientAgencyName = 'Numele agenției este obligatoriu';
      if (!viewingData.clientAgentName?.trim()) newErrors.clientAgentName = 'Numele agentului este obligatoriu';
      if (!viewingData.clientAgentPhone?.trim()) newErrors.clientAgentPhone = 'Telefonul agentului este obligatoriu';
    } else {
      if (!viewingData.leadId.trim()) newErrors.leadId = 'ID Lead este obligatoriu';
      if (!viewingData.clientName.trim()) newErrors.clientName = 'Numele este obligatoriu';
      if (!viewingData.clientPhone.trim()) {
        newErrors.clientPhone = 'Telefonul este obligatoriu';
      } else if (!/^07\d{8}$/.test(viewingData.clientPhone.replace(/\s/g, ''))) {
        newErrors.clientPhone = 'Format invalid (ex: 0712345678)';
      }
      if (needsClientContract) {
        if (!viewingData.clientContractOption) {
          newErrors.clientContract = 'Selectează o opțiune pentru contract';
        } else if (viewingData.clientContractOption === 'signed' && !viewingData.clientContractFile) {
          newErrors.clientContractFile = 'Încarcă contractul semnat';
        }
      }
    }
    // Validate Commission
    if (viewingData.commissionPaidBy === 'owner' || viewingData.commissionPaidBy === 'both') {
      if (!viewingData.ownerCommission || viewingData.ownerCommission <= 0) {
        newErrors.ownerCommission = 'Comisionul proprietar este obligatoriu';
      }
    }
    if (viewingData.commissionPaidBy === 'client' || viewingData.commissionPaidBy === 'both') {
      if (!viewingData.clientCommission || viewingData.clientCommission <= 0) {
        newErrors.clientCommission = 'Comisionul client este obligatoriu';
      }
    }
    // Validate Owner Data
    if (viewingData.ownerIsExternal) {
      if (!viewingData.ownerAgencyName?.trim()) newErrors.ownerAgencyName = 'Numele agenției este obligatoriu';
      if (!viewingData.ownerAgentName?.trim()) newErrors.ownerAgentName = 'Numele agentului este obligatoriu';
      if (!viewingData.ownerAgentPhone?.trim()) newErrors.ownerAgentPhone = 'Telefonul agentului este obligatoriu';
    } else {
      if (!viewingData.ownerId.trim()) newErrors.ownerId = 'ID Proprietar este obligatoriu';
      if (!viewingData.ownerName.trim()) newErrors.ownerName = 'Numele este obligatoriu';
      if (!viewingData.ownerPhone.trim()) {
        newErrors.ownerPhone = 'Telefonul este obligatoriu';
      } else if (!/^07\d{8}$/.test(viewingData.ownerPhone.replace(/\s/g, ''))) {
        newErrors.ownerPhone = 'Format invalid (ex: 0712345678)';
      }
      if (needsOwnerContract) {
        if (!viewingData.ownerContractOption) {
          newErrors.ownerContract = 'Selectează o opțiune pentru contract';
        } else if (viewingData.ownerContractOption === 'signed' && !viewingData.ownerContractFile) {
          newErrors.ownerContractFile = 'Încarcă contractul semnat';
        }
      }
    }
    // Validate Property Data
    // ID Proprietate is only required if owner is NOT external
    if (!viewingData.ownerIsExternal && !viewingData.propertyId.trim()) newErrors.propertyId = 'ID Proprietate este obligatoriu';
    if (!viewingData.propertyAddress.trim()) newErrors.propertyAddress = 'Adresa este obligatorie';
    if (!viewingData.propertyPrice || viewingData.propertyPrice <= 0) newErrors.propertyPrice = 'Prețul este obligatoriu';
    // Validate Viewing Date/Time
    if (!viewingData.viewingDate) newErrors.viewingDate = 'Data este obligatorie';
    if (!viewingData.viewingTime) newErrors.viewingTime = 'Ora este obligatorie';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) return;
    onSubmit(viewingData);
    onClose();
  };
  return <AnimatePresence data-id="element-2199">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-100" data-id="element-2200" />

          {/* Sidebar Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 bottom-0 w-full sm:w-[650px] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 shadow-2xl z-110 overflow-y-auto" data-id="element-2201">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white px-6 py-5 shadow-lg" data-id="element-2202">
              <div className="flex items-center justify-between" data-id="element-2203">
                <div data-id="element-2204">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-2205">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-2206">
                      <CalendarIcon className="w-5 h-5 text-white" data-id="element-2207" />
                    </div>
                    <div data-id="element-2208">
                      <h3 className="text-xl font-bold" data-id="element-2209">
                        {step === 'select_type' ? 'Selectează Tip Vizionare' : editMode ? 'Editează Vizionare' : 'Vizionare Nouă'}
                      </h3>
                      {step === 'form' && selectedType && <p className="text-sm text-blue-200" data-id="element-2210">
                          Tip:{' '}
                          {selectedType === 'inchiriere' ? 'Închiriere' : 'Vânzare'}
                        </p>}
                      {step === 'select_type' && <p className="text-sm text-blue-200" data-id="element-2211">
                          Alege tipul de tranzacție
                        </p>}
                    </div>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm" data-id="element-2212">
                  <XIcon className="w-6 h-6 text-white" data-id="element-2213" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6" data-id="element-2214">
              <AnimatePresence mode="wait" data-id="element-2215">
                {/* STEP 1: Select Type */}
                {step === 'select_type' && <motion.div key="select_type" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 20
            }} className="space-y-4" data-id="element-2216">
                    {/* Închiriere Option */}
                    <button onClick={() => handleSelectType('inchiriere')} className="w-full p-6 rounded-2xl border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 transition-all group shadow-lg hover:shadow-xl" data-id="element-2217">
                      <div className="flex items-center space-x-4" data-id="element-2218">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-2219">
                          <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2220">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" data-id="element-2221" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-2222">
                          <h4 className="text-lg font-bold text-[#132C53] mb-1" data-id="element-2223">
                            Închiriere
                          </h4>
                          <p className="text-sm text-slate-600" data-id="element-2224">
                            Vizionare pentru contract de închiriere
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* Vânzare Option */}
                    <div className="relative" data-id="element-2225">
                      <button onClick={() => hasCompletedRentalTransaction && handleSelectType('vanzare')} disabled={!hasCompletedRentalTransaction} className={`w-full p-6 rounded-2xl border-2 transition-all shadow-lg ${hasCompletedRentalTransaction ? 'border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 hover:shadow-xl cursor-pointer' : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'}`} data-id="element-2226">
                        <div className="flex items-center space-x-4" data-id="element-2227">
                          <div className={`w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg ${hasCompletedRentalTransaction ? 'group-hover:scale-110' : ''} transition-transform`} data-id="element-2228">
                            <HomeIcon className="w-8 h-8 text-white" data-id="element-2229" />
                          </div>
                          <div className="flex-1 text-left" data-id="element-2230">
                            <h4 className="text-lg font-bold text-[#132C53] mb-1" data-id="element-2231">
                              Vânzare
                            </h4>
                            <p className="text-sm text-slate-600" data-id="element-2232">
                              {hasCompletedRentalTransaction ? 'Vizionare pentru contract de vânzare' : 'Disponibil după prima tranzacție închiriere'}
                            </p>
                          </div>
                        </div>
                      </button>

                      {!hasCompletedRentalTransaction && <div className="absolute top-2 right-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold border border-amber-300" data-id="element-2233">
                          🔒 Blocat
                        </div>}
                    </div>
                  </motion.div>}

                {/* STEP 2: Form */}
                {step === 'form' && selectedType && <motion.div key="form" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-2234">
                    {/* Back Button (only if no initial type and not edit mode) */}
                    {!initialViewingType && !editMode && <button onClick={handleBack} className="flex items-center space-x-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors mb-4" data-id="element-2235">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2236">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" data-id="element-2237" />
                        </svg>
                        <span data-id="element-2238">Înapoi la selecție tip</span>
                      </button>}

                    {/* System Info Card */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2239">
                      <div className="flex items-center space-x-3 mb-4" data-id="element-2240">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center" data-id="element-2241">
                          <HashIcon className="w-6 h-6 text-white" data-id="element-2242" />
                        </div>
                        <div data-id="element-2243">
                          <div className="flex items-center space-x-2 mb-1" data-id="element-2244">
                            <span className="text-lg font-bold text-[#132C53]" data-id="element-2245">
                              {viewingData.viewingId}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${editMode ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-2246">
                              {editMode ? 'Editare' : 'Nouă'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-slate-600" data-id="element-2247">
                            <CalendarIcon className="w-4 h-4 text-purple-500" data-id="element-2248" />
                            <span data-id="element-2249">{viewingData.dateTime}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* A) Comisionare (was B) */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2
              }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2250">
                      <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2251">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-2252">
                          <DollarSignIcon className="w-5 h-5 text-purple-600" data-id="element-2253" />
                        </div>
                        <span data-id="element-2254">A) Comisionare</span>
                      </h5>

                      <div className="space-y-4" data-id="element-2255">
                        <div data-id="element-2256">
                          <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2257">
                            Cine plătește comisionul?{' '}
                            <span className="text-red-500" data-id="element-2258">*</span>
                          </label>
                          <div className="grid grid-cols-3 gap-2" data-id="element-2259">
                            <button type="button" onClick={() => handleCommissionChange('owner')} className={`px-4 py-3 rounded-xl text-sm font-bold transition-all ${viewingData.commissionPaidBy === 'owner' ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:border-purple-300'}`} data-id="element-2260">
                              Proprietar
                            </button>
                            <button type="button" onClick={() => handleCommissionChange('client')} className={`px-4 py-3 rounded-xl text-sm font-bold transition-all ${viewingData.commissionPaidBy === 'client' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300'}`} data-id="element-2261">
                              Client
                            </button>
                            <button type="button" onClick={() => handleCommissionChange('both')} className={`px-4 py-3 rounded-xl text-sm font-bold transition-all ${viewingData.commissionPaidBy === 'both' ? 'bg-orange-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300'}`} data-id="element-2262">
                              Ambele
                            </button>
                          </div>
                        </div>

                        {(viewingData.commissionPaidBy === 'owner' || viewingData.commissionPaidBy === 'both') && <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200 space-y-3" data-id="element-2263">
                            <label className="block text-xs font-medium text-purple-600 mb-2" data-id="element-2264">
                              Comision Proprietar (%){' '}
                              <span className="text-red-500" data-id="element-2265">*</span>
                            </label>
                            <input type="number" step="0.5" min="0" max="100" value={viewingData.ownerCommission || ''} onChange={e => {
                      updateData('ownerCommission', parseFloat(e.target.value));
                      updateData('ownerDiscountRequested', false);
                    }} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 outline-none bg-white ${errors.ownerCommission ? 'border-red-300' : 'border-purple-200'}`} placeholder="ex: 50" data-id="element-2266" />
                            {errors.ownerCommission && <p className="text-xs text-red-500 mt-1" data-id="element-2267">
                                ⚠️ {errors.ownerCommission}
                              </p>}

                            {/* TVA Options for Owner */}
                            <div className="pt-3 border-t border-purple-200" data-id="element-2268">
                              <label className="block text-xs font-medium text-purple-600 mb-2" data-id="element-2269">
                                Situație TVA{' '}
                                <span className="text-red-500" data-id="element-2270">*</span>
                              </label>
                              <div className="grid grid-cols-2 gap-2" data-id="element-2271">
                                <button type="button" onClick={() => {
                          updateData('ownerVatOption', 'included');
                          updateData('ownerDiscountRequested', false);
                        }} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${viewingData.ownerVatOption === 'included' ? 'bg-purple-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-purple-200 hover:border-purple-400'}`} data-id="element-2272">
                                  TVA Inclus
                                </button>
                                <button type="button" onClick={() => {
                          updateData('ownerVatOption', 'additional');
                          updateData('ownerDiscountRequested', false);
                        }} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${viewingData.ownerVatOption === 'additional' ? 'bg-purple-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-purple-200 hover:border-purple-400'}`} data-id="element-2273">
                                  + TVA (21%)
                                </button>
                              </div>
                              <p className="text-xs text-purple-600 mt-2" data-id="element-2274">
                                {viewingData.ownerVatOption === 'included' && '• Minim: 50% (TVA inclus în procent)'}
                                {viewingData.ownerVatOption === 'additional' && '• Minim: 40% (TVA se adaugă separat)'}
                              </p>
                            </div>

                            {/* Discount Warning/Request */}
                            {needsOwnerDiscount() && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} className="pt-3 border-t border-purple-200" data-id="element-2275">
                                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-id="element-2276">
                                  <div className="flex items-start space-x-3 mb-3" data-id="element-2277">
                                    <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2278" />
                                    <div className="flex-1" data-id="element-2279">
                                      <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-2280">
                                        Comision sub minim
                                      </p>
                                      <p className="text-xs text-amber-700" data-id="element-2281">
                                        Comisionul introdus (
                                        {viewingData.ownerCommission}%) este sub
                                        minimul permis (
                                        {viewingData.ownerVatOption === 'included' ? '50%' : '40%'}
                                        ). Este necesară aprobarea agenției.
                                      </p>
                                    </div>
                                  </div>
                                  {!viewingData.ownerDiscountRequested ? <button type="button" onClick={handleRequestOwnerDiscount} className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 transition-colors flex items-center justify-center space-x-2" data-id="element-2282">
                                      <FileTextIcon className="w-4 h-4" data-id="element-2283" />
                                      <span data-id="element-2284">Solicită Derogare Discount</span>
                                    </button> : <div className="flex items-center space-x-2 p-3 bg-emerald-50 border border-emerald-300 rounded-lg" data-id="element-2285">
                                      <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-2286" />
                                      <span className="text-sm font-bold text-emerald-900" data-id="element-2287">
                                        Derogare solicitată - În așteptare
                                      </span>
                                    </div>}
                                </div>
                              </motion.div>}
                          </div>}

                        {(viewingData.commissionPaidBy === 'client' || viewingData.commissionPaidBy === 'both') && <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 space-y-3" data-id="element-2288">
                            <label className="block text-xs font-medium text-blue-600 mb-2" data-id="element-2289">
                              Comision Client (%){' '}
                              <span className="text-red-500" data-id="element-2290">*</span>
                            </label>
                            <input type="number" step="0.5" min="0" max="100" value={viewingData.clientCommission || ''} onChange={e => {
                      updateData('clientCommission', parseFloat(e.target.value));
                      updateData('clientDiscountRequested', false);
                    }} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white ${errors.clientCommission ? 'border-red-300' : 'border-blue-200'}`} placeholder="ex: 50" data-id="element-2291" />
                            {errors.clientCommission && <p className="text-xs text-red-500 mt-1" data-id="element-2292">
                                ⚠️ {errors.clientCommission}
                              </p>}

                            {/* TVA Options for Client */}
                            <div className="pt-3 border-t border-blue-200" data-id="element-2293">
                              <label className="block text-xs font-medium text-blue-600 mb-2" data-id="element-2294">
                                Situație TVA{' '}
                                <span className="text-red-500" data-id="element-2295">*</span>
                              </label>
                              <div className="grid grid-cols-2 gap-2" data-id="element-2296">
                                <button type="button" onClick={() => {
                          updateData('clientVatOption', 'included');
                          updateData('clientDiscountRequested', false);
                        }} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${viewingData.clientVatOption === 'included' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-blue-200 hover:border-blue-400'}`} data-id="element-2297">
                                  TVA Inclus
                                </button>
                                <button type="button" onClick={() => {
                          updateData('clientVatOption', 'additional');
                          updateData('clientDiscountRequested', false);
                        }} className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${viewingData.clientVatOption === 'additional' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-blue-200 hover:border-blue-400'}`} data-id="element-2298">
                                  + TVA (21%)
                                </button>
                              </div>
                              <p className="text-xs text-blue-600 mt-2" data-id="element-2299">
                                {viewingData.clientVatOption === 'included' && '• Minim: 50% (TVA inclus în procent)'}
                                {viewingData.clientVatOption === 'additional' && '• Minim: 40% (TVA se adaugă separat)'}
                              </p>
                            </div>

                            {/* Discount Warning/Request */}
                            {needsClientDiscount() && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} className="pt-3 border-t border-blue-200" data-id="element-2300">
                                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-id="element-2301">
                                  <div className="flex items-start space-x-3 mb-3" data-id="element-2302">
                                    <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2303" />
                                    <div className="flex-1" data-id="element-2304">
                                      <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-2305">
                                        Comision sub minim
                                      </p>
                                      <p className="text-xs text-amber-700" data-id="element-2306">
                                        Comisionul introdus (
                                        {viewingData.clientCommission}%) este
                                        sub minimul permis (
                                        {viewingData.clientVatOption === 'included' ? '50%' : '40%'}
                                        ). Este necesară aprobarea agenției.
                                      </p>
                                    </div>
                                  </div>
                                  {!viewingData.clientDiscountRequested ? <button type="button" onClick={handleRequestClientDiscount} className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 transition-colors flex items-center justify-center space-x-2" data-id="element-2307">
                                      <FileTextIcon className="w-4 h-4" data-id="element-2308" />
                                      <span data-id="element-2309">Solicită Derogare Discount</span>
                                    </button> : <div className="flex items-center space-x-2 p-3 bg-emerald-50 border border-emerald-300 rounded-lg" data-id="element-2310">
                                      <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-2311" />
                                      <span className="text-sm font-bold text-emerald-900" data-id="element-2312">
                                        Derogare solicitată - În așteptare
                                      </span>
                                    </div>}
                                </div>
                              </motion.div>}
                          </div>}
                      </div>
                    </motion.div>

                    {/* B) Date Client (was A) */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3
              }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2313">
                      <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2314">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-2315">
                          <UserIcon className="w-5 h-5 text-blue-600" data-id="element-2316" />
                        </div>
                        <span data-id="element-2317">B) Date Client</span>
                      </h5>

                      <div className="space-y-4" data-id="element-2318">
                        {/* External Agency Checkbox */}
                        <div onClick={toggleClientExternal} className="flex items-center space-x-3 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-blue-300 transition-all cursor-pointer" data-id="element-2319">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${viewingData.clientIsExternal ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`} data-id="element-2320">
                            {viewingData.clientIsExternal && <CheckIcon className="w-3.5 h-3.5 text-white stroke-[3]" data-id="element-2321" />}
                          </div>
                          <BuildingIcon className="w-4 h-4 text-blue-600 flex-shrink-0" data-id="element-2322" />
                          <span className="text-sm font-bold text-slate-900 flex-1" data-id="element-2323">
                            Client reprezentat de agenție externă (colaborare)
                          </span>
                        </div>

                        {viewingData.clientIsExternal ? <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} className="space-y-4" data-id="element-2324">
                            <div data-id="element-2325">
                              <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2326">
                                Nume Agenție{' '}
                                <span className="text-red-500" data-id="element-2327">*</span>
                              </label>
                              <input type="text" value={viewingData.clientAgencyName || ''} onChange={e => updateData('clientAgencyName', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.clientAgencyName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: RE/MAX Premium" data-id="element-2328" />
                              {errors.clientAgencyName && <p className="text-xs text-red-500 mt-1" data-id="element-2329">
                                  ⚠️ {errors.clientAgencyName}
                                </p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3" data-id="element-2330">
                              <div data-id="element-2331">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2332">
                                  Nume Agent{' '}
                                  <span className="text-red-500" data-id="element-2333">*</span>
                                </label>
                                <input type="text" value={viewingData.clientAgentName || ''} onChange={e => updateData('clientAgentName', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.clientAgentName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Ana Popescu" data-id="element-2334" />
                                {errors.clientAgentName && <p className="text-xs text-red-500 mt-1" data-id="element-2335">
                                    ⚠️ {errors.clientAgentName}
                                  </p>}
                              </div>
                              <div data-id="element-2336">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2337">
                                  Telefon Agent{' '}
                                  <span className="text-red-500" data-id="element-2338">*</span>
                                </label>
                                <input type="tel" value={viewingData.clientAgentPhone || ''} onChange={e => updateData('clientAgentPhone', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.clientAgentPhone ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="07XXXXXXXX" data-id="element-2339" />
                                {errors.clientAgentPhone && <p className="text-xs text-red-500 mt-1" data-id="element-2340">
                                    ⚠️ {errors.clientAgentPhone}
                                  </p>}
                              </div>
                            </div>
                          </motion.div> : <>
                            <div data-id="element-2341">
                              <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2342">
                                ID Lead/Cerere{' '}
                                <span className="text-red-500" data-id="element-2343">*</span>
                              </label>
                              <input type="text" value={viewingData.leadId} onChange={e => updateData('leadId', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.leadId ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: LI00001" data-id="element-2344" />
                              {errors.leadId && <p className="text-xs text-red-500 mt-1" data-id="element-2345">
                                  ⚠️ {errors.leadId}
                                </p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3" data-id="element-2346">
                              <div data-id="element-2347">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2348">
                                  Nume Client{' '}
                                  <span className="text-red-500" data-id="element-2349">*</span>
                                </label>
                                <input type="text" value={viewingData.clientName} onChange={e => updateData('clientName', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.clientName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Maria Ionescu" data-id="element-2350" />
                                {errors.clientName && <p className="text-xs text-red-500 mt-1" data-id="element-2351">
                                    ⚠️ {errors.clientName}
                                  </p>}
                              </div>
                              <div data-id="element-2352">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2353">
                                  Telefon{' '}
                                  <span className="text-red-500" data-id="element-2354">*</span>
                                </label>
                                <div className="relative" data-id="element-2355">
                                  <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2356" />
                                  <input type="tel" value={viewingData.clientPhone} onChange={e => updateData('clientPhone', e.target.value)} className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.clientPhone ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="07XXXXXXXX" data-id="element-2357" />
                                </div>
                                {errors.clientPhone && <p className="text-xs text-red-500 mt-1" data-id="element-2358">
                                    ⚠️ {errors.clientPhone}
                                  </p>}
                              </div>
                            </div>

                            {/* Contract Client - Only if commissioned */}
                            {needsClientContract && <div className="pt-4 border-t border-slate-200" data-id="element-2359">
                                <label className="block text-xs font-medium text-slate-500 mb-3" data-id="element-2360">
                                  Contract Client{' '}
                                  <span className="text-red-500" data-id="element-2361">*</span>
                                </label>
                                <div className="space-y-3" data-id="element-2362">
                                  {/* Option 1: Signed */}
                                  <div onClick={() => updateData('clientContractOption', 'signed')} className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${viewingData.clientContractOption === 'signed' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-200'}`} data-id="element-2363">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 transition-colors ${viewingData.clientContractOption === 'signed' ? 'border-blue-600 bg-white' : 'border-slate-300 bg-white'}`} data-id="element-2364">
                                      {viewingData.clientContractOption === 'signed' && <div className="w-2.5 h-2.5 bg-blue-600 rounded-full" data-id="element-2365" />}
                                    </div>
                                    <div className="flex-1" data-id="element-2366">
                                      <p className="text-sm font-bold text-slate-900 mb-1" data-id="element-2367">
                                        Contract Semnat (încarcă documentul)
                                      </p>
                                      {viewingData.clientContractOption === 'signed' && <motion.div initial={{
                              opacity: 0,
                              height: 0
                            }} animate={{
                              opacity: 1,
                              height: 'auto'
                            }} className="mt-3" onClick={e => e.stopPropagation()} data-id="element-2368">
                                          <label className="block cursor-pointer" data-id="element-2369">
                                            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors bg-white" data-id="element-2370">
                                              <input type="file" accept=".pdf,.doc,.docx" onChange={e => {
                                    if (e.target.files?.[0]) {
                                      handleFileUpload('clientContractFile', e.target.files[0]);
                                    }
                                  }} className="hidden" data-id="element-2371" />
                                              <UploadIcon className="w-6 h-6 mx-auto mb-2 text-blue-600" data-id="element-2372" />
                                              <p className="text-sm font-bold text-blue-900" data-id="element-2373">
                                                {viewingData.clientContractFile ? viewingData.clientContractFile.name : 'Încarcă Contract (PDF, DOC, DOCX)'}
                                              </p>
                                              {!viewingData.clientContractFile && <p className="text-xs text-red-600 mt-1" data-id="element-2374">
                                                  * Obligatoriu
                                                </p>}
                                            </div>
                                          </label>
                                        </motion.div>}
                                    </div>
                                  </div>

                                  {/* Option 2: Physical Signing */}
                                  <div onClick={() => {
                          updateData('clientContractOption', 'physical_signing');
                          updateData('clientContractFile', null);
                        }} className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${viewingData.clientContractOption === 'physical_signing' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'}`} data-id="element-2375">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 transition-colors ${viewingData.clientContractOption === 'physical_signing' ? 'border-emerald-600 bg-white' : 'border-slate-300 bg-white'}`} data-id="element-2376">
                                      {viewingData.clientContractOption === 'physical_signing' && <div className="w-2.5 h-2.5 bg-emerald-600 rounded-full" data-id="element-2377" />}
                                    </div>
                                    <div className="flex-1" data-id="element-2378">
                                      <p className="text-sm font-bold text-slate-900 mb-1" data-id="element-2379">
                                        Semnare Fizică
                                      </p>
                                      <p className="text-xs text-slate-600" data-id="element-2380">
                                        Contractul va fi semnat fizic la
                                        vizionare
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {errors.clientContract && <p className="text-xs text-red-500 mt-2" data-id="element-2381">
                                    ⚠️ {errors.clientContract}
                                  </p>}
                                {errors.clientContractFile && <p className="text-xs text-red-500 mt-2" data-id="element-2382">
                                    ⚠️ {errors.clientContractFile}
                                  </p>}
                              </div>}
                          </>}
                      </div>
                    </motion.div>

                    {/* C) Date Proprietar (unchanged) */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4
              }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2383">
                      <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2384">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-2385">
                          <UserIcon className="w-5 h-5 text-emerald-600" data-id="element-2386" />
                        </div>
                        <span data-id="element-2387">C) Date Proprietar</span>
                      </h5>

                      <div className="space-y-4" data-id="element-2388">
                        {/* External Agency Checkbox */}
                        <div onClick={toggleOwnerExternal} className="flex items-center space-x-3 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-emerald-300 transition-all cursor-pointer" data-id="element-2389">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${viewingData.ownerIsExternal ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white'}`} data-id="element-2390">
                            {viewingData.ownerIsExternal && <CheckIcon className="w-3.5 h-3.5 text-white stroke-[3]" data-id="element-2391" />}
                          </div>
                          <BuildingIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-2392" />
                          <span className="text-sm font-bold text-slate-900 flex-1" data-id="element-2393">
                            Proprietar reprezentat de agenție externă
                            (colaborare)
                          </span>
                        </div>

                        {viewingData.ownerIsExternal ? <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} className="space-y-4" data-id="element-2394">
                            <div data-id="element-2395">
                              <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2396">
                                Nume Agenție{' '}
                                <span className="text-red-500" data-id="element-2397">*</span>
                              </label>
                              <input type="text" value={viewingData.ownerAgencyName || ''} onChange={e => updateData('ownerAgencyName', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${errors.ownerAgencyName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Century 21" data-id="element-2398" />
                              {errors.ownerAgencyName && <p className="text-xs text-red-500 mt-1" data-id="element-2399">
                                  ⚠️ {errors.ownerAgencyName}
                                </p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3" data-id="element-2400">
                              <div data-id="element-2401">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2402">
                                  Nume Agent{' '}
                                  <span className="text-red-500" data-id="element-2403">*</span>
                                </label>
                                <input type="text" value={viewingData.ownerAgentName || ''} onChange={e => updateData('ownerAgentName', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${errors.ownerAgentName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Ion Popescu" data-id="element-2404" />
                                {errors.ownerAgentName && <p className="text-xs text-red-500 mt-1" data-id="element-2405">
                                    ⚠️ {errors.ownerAgentName}
                                  </p>}
                              </div>
                              <div data-id="element-2406">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2407">
                                  Telefon Agent{' '}
                                  <span className="text-red-500" data-id="element-2408">*</span>
                                </label>
                                <input type="tel" value={viewingData.ownerAgentPhone || ''} onChange={e => updateData('ownerAgentPhone', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${errors.ownerAgentPhone ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="07XXXXXXXX" data-id="element-2409" />
                                {errors.ownerAgentPhone && <p className="text-xs text-red-500 mt-1" data-id="element-2410">
                                    ⚠️ {errors.ownerAgentPhone}
                                  </p>}
                              </div>
                            </div>
                          </motion.div> : <>
                            <div data-id="element-2411">
                              <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2412">
                                ID Proprietar{' '}
                                <span className="text-red-500" data-id="element-2413">*</span>
                              </label>
                              <input type="text" value={viewingData.ownerId} onChange={e => updateData('ownerId', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${errors.ownerId ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: PR00001" data-id="element-2414" />
                              {errors.ownerId && <p className="text-xs text-red-500 mt-1" data-id="element-2415">
                                  ⚠️ {errors.ownerId}
                                </p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3" data-id="element-2416">
                              <div data-id="element-2417">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2418">
                                  Nume Proprietar{' '}
                                  <span className="text-red-500" data-id="element-2419">*</span>
                                </label>
                                <input type="text" value={viewingData.ownerName} onChange={e => updateData('ownerName', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${errors.ownerName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Ion Popescu" data-id="element-2420" />
                                {errors.ownerName && <p className="text-xs text-red-500 mt-1" data-id="element-2421">
                                    ⚠️ {errors.ownerName}
                                  </p>}
                              </div>
                              <div data-id="element-2422">
                                <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2423">
                                  Telefon{' '}
                                  <span className="text-red-500" data-id="element-2424">*</span>
                                </label>
                                <div className="relative" data-id="element-2425">
                                  <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2426" />
                                  <input type="tel" value={viewingData.ownerPhone} onChange={e => updateData('ownerPhone', e.target.value)} className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${errors.ownerPhone ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="07XXXXXXXX" data-id="element-2427" />
                                </div>
                                {errors.ownerPhone && <p className="text-xs text-red-500 mt-1" data-id="element-2428">
                                    ⚠️ {errors.ownerPhone}
                                  </p>}
                              </div>
                            </div>

                            {/* Contract Proprietar - Only if commissioned */}
                            {needsOwnerContract && <div className="pt-4 border-t border-slate-200" data-id="element-2429">
                                <label className="block text-xs font-medium text-slate-500 mb-3" data-id="element-2430">
                                  Contract Proprietar{' '}
                                  <span className="text-red-500" data-id="element-2431">*</span>
                                </label>
                                <div className="space-y-3" data-id="element-2432">
                                  {/* Option 1: Signed */}
                                  <div onClick={() => updateData('ownerContractOption', 'signed')} className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${viewingData.ownerContractOption === 'signed' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'}`} data-id="element-2433">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 transition-colors ${viewingData.ownerContractOption === 'signed' ? 'border-emerald-600 bg-white' : 'border-slate-300 bg-white'}`} data-id="element-2434">
                                      {viewingData.ownerContractOption === 'signed' && <div className="w-2.5 h-2.5 bg-emerald-600 rounded-full" data-id="element-2435" />}
                                    </div>
                                    <div className="flex-1" data-id="element-2436">
                                      <p className="text-sm font-bold text-slate-900 mb-1" data-id="element-2437">
                                        Contract Semnat (încarcă documentul)
                                      </p>
                                      {viewingData.ownerContractOption === 'signed' && <motion.div initial={{
                              opacity: 0,
                              height: 0
                            }} animate={{
                              opacity: 1,
                              height: 'auto'
                            }} className="mt-3" onClick={e => e.stopPropagation()} data-id="element-2438">
                                          <label className="block cursor-pointer" data-id="element-2439">
                                            <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 text-center hover:border-emerald-500 transition-colors bg-white" data-id="element-2440">
                                              <input type="file" accept=".pdf,.doc,.docx" onChange={e => {
                                    if (e.target.files?.[0]) {
                                      handleFileUpload('ownerContractFile', e.target.files[0]);
                                    }
                                  }} className="hidden" data-id="element-2441" />
                                              <UploadIcon className="w-6 h-6 mx-auto mb-2 text-emerald-600" data-id="element-2442" />
                                              <p className="text-sm font-bold text-emerald-900" data-id="element-2443">
                                                {viewingData.ownerContractFile ? viewingData.ownerContractFile.name : 'Încarcă Contract (PDF, DOC, DOCX)'}
                                              </p>
                                              {!viewingData.ownerContractFile && <p className="text-xs text-red-600 mt-1" data-id="element-2444">
                                                  * Obligatoriu
                                                </p>}
                                            </div>
                                          </label>
                                        </motion.div>}
                                    </div>
                                  </div>

                                  {/* Option 2: Physical Signing */}
                                  <div onClick={() => {
                          updateData('ownerContractOption', 'physical_signing');
                          updateData('ownerContractFile', null);
                        }} className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${viewingData.ownerContractOption === 'physical_signing' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-200'}`} data-id="element-2445">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 transition-colors ${viewingData.ownerContractOption === 'physical_signing' ? 'border-purple-600 bg-white' : 'border-slate-300 bg-white'}`} data-id="element-2446">
                                      {viewingData.ownerContractOption === 'physical_signing' && <div className="w-2.5 h-2.5 bg-purple-600 rounded-full" data-id="element-2447" />}
                                    </div>
                                    <div className="flex-1" data-id="element-2448">
                                      <p className="text-sm font-bold text-slate-900 mb-1" data-id="element-2449">
                                        Semnare Fizică
                                      </p>
                                      <p className="text-xs text-slate-600" data-id="element-2450">
                                        Contractul va fi semnat fizic la
                                        vizionare
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {errors.ownerContract && <p className="text-xs text-red-500 mt-2" data-id="element-2451">
                                    ⚠️ {errors.ownerContract}
                                  </p>}
                                {errors.ownerContractFile && <p className="text-xs text-red-500 mt-2" data-id="element-2452">
                                    ⚠️ {errors.ownerContractFile}
                                  </p>}
                              </div>}
                          </>}
                      </div>
                    </motion.div>

                    {/* D) Date Proprietate */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5
              }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2453">
                      <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2454">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center" data-id="element-2455">
                          <HomeIcon className="w-5 h-5 text-amber-600" data-id="element-2456" />
                        </div>
                        <span data-id="element-2457">D) Date Proprietate</span>
                      </h5>

                      <div className="space-y-4" data-id="element-2458">
                        {/* ID Proprietate - Only if owner is NOT external */}
                        {!viewingData.ownerIsExternal ? <div data-id="element-2459">
                            <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2460">
                              ID Proprietate{' '}
                              <span className="text-red-500" data-id="element-2461">*</span>
                            </label>
                            <input type="text" value={viewingData.propertyId} onChange={e => updateData('propertyId', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-amber-500 outline-none transition-all ${errors.propertyId ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: P98772" data-id="element-2462" />
                            {errors.propertyId && <p className="text-xs text-red-500 mt-1" data-id="element-2463">
                                ⚠️ {errors.propertyId}
                              </p>}
                          </div> : <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200" data-id="element-2464">
                            <div className="flex items-center space-x-3" data-id="element-2465">
                              <BuildingIcon className="w-5 h-5 text-purple-600 flex-shrink-0" data-id="element-2466" />
                              <div data-id="element-2467">
                                <p className="text-sm font-bold text-purple-900" data-id="element-2468">
                                  Proprietate altă agenție
                                </p>
                                <p className="text-xs text-purple-700" data-id="element-2469">
                                  Proprietatea este gestionată de{' '}
                                  {viewingData.ownerAgencyName}
                                </p>
                              </div>
                            </div>
                          </div>}

                        <div data-id="element-2470">
                          <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2471">
                            Adresă Exactă{' '}
                            <span className="text-red-500" data-id="element-2472">*</span>
                          </label>
                          <input type="text" value={viewingData.propertyAddress} onChange={e => updateData('propertyAddress', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-amber-500 outline-none transition-all ${errors.propertyAddress ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Str. Titan 45, Sector 3, București" data-id="element-2473" />
                          {errors.propertyAddress && <p className="text-xs text-red-500 mt-1" data-id="element-2474">
                              ⚠️ {errors.propertyAddress}
                            </p>}
                        </div>
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200" data-id="element-2475">
                          <label className="block text-xs font-medium text-amber-600 mb-2" data-id="element-2476">
                            Preț Proprietate (EUR){' '}
                            <span className="text-red-500" data-id="element-2477">*</span>
                          </label>
                          <div className="relative" data-id="element-2478">
                            <DollarSignIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400" data-id="element-2479" />
                            <input type="number" min="0" value={viewingData.propertyPrice || ''} onChange={e => updateData('propertyPrice', parseFloat(e.target.value))} className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-amber-500 outline-none bg-white ${errors.propertyPrice ? 'border-red-300' : 'border-amber-200'}`} placeholder="ex: 450" data-id="element-2480" />
                          </div>
                          {errors.propertyPrice && <p className="text-xs text-red-500 mt-1" data-id="element-2481">
                              ⚠️ {errors.propertyPrice}
                            </p>}
                        </div>
                      </div>
                    </motion.div>

                    {/* E) Data și Ora Vizionare */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.6
              }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2482">
                      <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2483">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-2484">
                          <CalendarIcon className="w-5 h-5 text-indigo-600" data-id="element-2485" />
                        </div>
                        <span data-id="element-2486">E) Data și Ora Vizionare</span>
                      </h5>

                      <div className="space-y-4" data-id="element-2487">
                        <div className="grid grid-cols-2 gap-4" data-id="element-2488">
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2489">
                            <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2490">
                              Data <span className="text-red-500" data-id="element-2491">*</span>
                            </label>
                            <input type="date" value={viewingData.viewingDate} onChange={e => updateData('viewingDate', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white ${errors.viewingDate ? 'border-red-300' : 'border-slate-200'}`} data-id="element-2492" />
                            {errors.viewingDate && <p className="text-xs text-red-500 mt-1" data-id="element-2493">
                                ⚠️ {errors.viewingDate}
                              </p>}
                          </div>
                          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200" data-id="element-2494">
                            <label className="block text-xs font-medium text-indigo-600 mb-2" data-id="element-2495">
                              Ora <span className="text-red-500" data-id="element-2496">*</span>
                            </label>
                            <input type="time" value={viewingData.viewingTime} onChange={e => updateData('viewingTime', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white ${errors.viewingTime ? 'border-red-300' : 'border-indigo-200'}`} data-id="element-2497" />
                            {errors.viewingTime && <p className="text-xs text-red-500 mt-1" data-id="element-2498">
                                ⚠️ {errors.viewingTime}
                              </p>}
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2499">
                          <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2500">
                            Observații (opțional)
                          </label>
                          <textarea value={viewingData.notes} onChange={e => updateData('notes', e.target.value)} rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none resize-none bg-white" placeholder="Detalii suplimentare despre vizionare..." data-id="element-2501" />
                        </div>
                      </div>
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.7
              }} className="flex space-x-3 pb-6" data-id="element-2502">
                      <button onClick={onClose} className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 bg-white border-2 border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-2503">
                        Anulează
                      </button>
                      <button onClick={handleSubmit} className="flex-1 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center gap-2" data-id="element-2504">
                        <SaveIcon className="w-5 h-5" data-id="element-2505" />
                        {editMode ? 'Salvează Modificări' : 'Salvează Vizionare'}
                      </button>
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}